/**
 * /menus — one directory of every place on what Finland that actually has a
 * menu (link, PDF, photo or dish rows). Browsable by cuisine, price range and
 * region. Like every hub, it references the single `places` record; nothing is
 * duplicated here.
 */
import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { ChevronDown, Search, UtensilsCrossed } from "lucide-react";

import { getMenusDirectory, type MenuDirectoryEntry } from "@/lib/hubs.functions";
import { SmartImage } from "@/components/ui/SmartImage";

export const menusQuery = queryOptions({
  queryKey: ["public-menus-directory"],
  queryFn: () => getMenusDirectory(),
});

const T = {
  en: {
    eyebrow: "Finland-wide • Menus",
    title: "Menus",
    tagline: "Every restaurant, sauna and resort on what Finland with a published menu.",
    search: "Search by name or cuisine…",
    cuisine: "All cuisines",
    price: "All prices",
    region: "All regions",
    showing: (n: number) => `Showing ${n} menus`,
    dishes: (n: number) => `${n} dishes`,
    view: "View menu",
    empty: "No menus match those filters.",
  },
  fi: {
    eyebrow: "Teemasivu • Ruokalistat",
    title: "Ruokalistat",
    tagline: "Kaikki what Finlandin kohteet, joilla on julkaistu ruokalista.",
    search: "Hae nimellä tai keittiöllä…",
    cuisine: "Kaikki keittiöt",
    price: "Kaikki hintatasot",
    region: "Kaikki alueet",
    showing: (n: number) => `Näytetään ${n} ruokalistaa`,
    dishes: (n: number) => `${n} annosta`,
    view: "Katso ruokalista",
    empty: "Ei osumia näillä suodattimilla.",
  },
} as const;

function uniq(values: (string | null)[]): string[] {
  return Array.from(new Set(values.filter((v): v is string => Boolean(v && v.trim())))).sort((a, b) =>
    a.localeCompare(b),
  );
}

function regionOf(e: MenuDirectoryEntry): string | null {
  if (!e.city_slug) return e.area;
  return e.city_slug.charAt(0).toUpperCase() + e.city_slug.slice(1);
}

export function MenusHubPage({ locale }: { locale: "en" | "fi" }) {
  const { data } = useSuspenseQuery(menusQuery) as { data: MenuDirectoryEntry[] };
  const t = T[locale];
  const [q, setQ] = useState("");
  const [cuisine, setCuisine] = useState("all");
  const [price, setPrice] = useState("all");
  const [region, setRegion] = useState("all");

  const entries = data ?? [];
  const cuisines = useMemo(() => uniq(entries.map((e) => e.cuisine)), [entries]);
  const prices = useMemo(() => uniq(entries.map((e) => e.price)), [entries]);
  const regions = useMemo(() => uniq(entries.map(regionOf)), [entries]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return entries.filter((e) => {
      if (cuisine !== "all" && e.cuisine !== cuisine) return false;
      if (price !== "all" && e.price !== price) return false;
      if (region !== "all" && regionOf(e) !== region) return false;
      if (!needle) return true;
      return (
        e.name.toLowerCase().includes(needle) || (e.cuisine ?? "").toLowerCase().includes(needle)
      );
    });
  }, [entries, q, cuisine, price, region]);

  const selectCls =
    "cursor-pointer appearance-none bg-transparent px-5 py-4 pr-9 text-sm font-medium text-foreground outline-none";

  return (
    <main className="min-h-screen bg-background">
      <div className="mx-auto w-full max-w-7xl px-6 py-14 sm:px-12 sm:py-20">
        <header className="mb-12">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-primary">{t.eyebrow}</p>
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div className="max-w-2xl">
              <h1 className="font-heading text-5xl leading-[1.05] text-foreground md:text-7xl">{t.title}</h1>
              <p className="mt-4 text-lg font-medium text-muted-foreground">{t.tagline}</p>
            </div>
            <span className="text-sm font-medium text-muted-foreground">{t.showing(filtered.length)}</span>
          </div>

          <div className="mt-10 flex flex-col gap-1 rounded-2xl border border-border bg-muted/60 p-2 md:flex-row md:items-stretch">
            <label className="relative flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t.search}
                aria-label={t.search}
                className="w-full bg-transparent py-4 pl-12 pr-4 text-foreground outline-none placeholder:text-muted-foreground"
              />
            </label>
            {[
              { value: cuisine, set: setCuisine, all: t.cuisine, options: cuisines },
              { value: price, set: setPrice, all: t.price, options: prices },
              { value: region, set: setRegion, all: t.region, options: regions },
            ]
              .filter((f) => f.options.length > 0)
              .map((f) => (
                <div key={f.all} className="relative flex items-center border-t border-border md:border-l md:border-t-0">
                  <select
                    value={f.value}
                    onChange={(e) => f.set(e.target.value)}
                    aria-label={f.all}
                    className={selectCls}
                  >
                    <option value="all">{f.all}</option>
                    {f.options.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 h-4 w-4 text-muted-foreground" />
                </div>
              ))}
          </div>
        </header>

        <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((e) => (
            <li key={e.id}>
              <Link
                to="/menu/$slug"
                params={{ slug: e.slug }}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="aspect-[16/10] overflow-hidden bg-muted">
                  {e.image_url ? (
                    <SmartImage
                      src={e.image_url}
                      alt={e.name}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-muted-foreground">
                      <UtensilsCrossed className="h-8 w-8" />
                    </div>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h2 className="font-heading text-xl text-foreground">{e.name}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {[e.cuisine, regionOf(e), e.price].filter(Boolean).join(" · ")}
                  </p>
                  <div className="mt-4 flex items-center justify-between text-xs">
                    <span className="rounded-full bg-muted px-3 py-1 font-semibold uppercase tracking-wide text-muted-foreground">
                      {e.dish_count > 0 ? t.dishes(e.dish_count) : t.view}
                    </span>
                    <span className="font-semibold text-primary">{t.view} →</span>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
        {filtered.length === 0 && <p className="py-16 text-center text-sm text-muted-foreground">{t.empty}</p>}
      </div>
    </main>
  );
}
